// 줄 끝 '⋯' 메뉴 — 자주 안 쓰는 동작(되돌리기 · 스페어 전환)을 접어 둔다(2026-10-28 오너 "로봇 정보 정리").
//
// 메뉴는 화면 기준(fixed)으로 띄운다. 표를 감싼 칸이 가로 스크롤(overflow)이라 그 안에 absolute 로 두면 아래 줄에서
// 메뉴가 잘린다.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/Icon';

export interface RowMoreItem {
  label: string;
  /** 다른 화면으로 가는 항목은 href — 링크로 그려 새 탭 열기(Ctrl+클릭)도 된다. */
  onClick?: () => void;
  href?: string;
  testId?: string;
  title?: string;
  disabled?: boolean;
  danger?: boolean;
  /** 이 항목 위에 가는 구분선(묶음 나누기 — 로봇 상세 머리의 ⋯). */
  separatorBefore?: boolean;
}

export function RowMoreMenu({
  items,
  label,
  testId,
  size = 28,
}: {
  items: RowMoreItem[];
  /** 단추 한 변(px). 줄 끝은 28, 로봇 상세 머리는 옆 단추와 같은 32. */
  size?: number;
  /** 스크린리더용 — 예: '#2103 동작 더 보기'. */
  label: string;
  testId?: string;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!pos) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !btnRef.current?.contains(t)) setPos(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    // 스크롤하면 버튼과 메뉴가 어긋난다 — 닫는다.
    const onScroll = () => setPos(null);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [pos]);

  if (items.length === 0) return null;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={pos != null}
        data-testid={testId}
        className="row-more-btn"
        style={{ ...moreBtn, width: size, height: size }}
        onClick={() => {
          if (pos) return setPos(null);
          const r = btnRef.current?.getBoundingClientRect();
          if (r) setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
        }}
      >
        <Icon name="more" size={16} aria-hidden />
      </button>
      {pos && (
        <div ref={menuRef} role="menu" style={{ ...menu, top: pos.top, right: pos.right }}>
          {items.map((it) => [
            it.separatorBefore && <hr key={`${it.label}-sep`} style={sep} />,
            it.href ? (
              <Link
                key={it.label}
                to={it.href}
                role="menuitem"
                data-testid={it.testId}
                title={it.title}
                className="row-more-item"
                style={{ textDecoration: 'none' }}
                onClick={() => setPos(null)}
              >
                {it.label}
              </Link>
            ) : (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                data-testid={it.testId}
                title={it.title}
                // 꺼진 항목은 마우스를 올려도 title 이 안 뜨는 브라우저가 있다 — 이유를 스크린리더에도 준다.
                aria-description={it.title}
                disabled={it.disabled}
                // 2026-11-02 오너 — 커서가 올라간 항목에 색이 들어온다(globals.css .row-more-item).
                className={it.danger ? 'row-more-item danger' : 'row-more-item'}
                onClick={() => {
                  setPos(null);
                  it.onClick?.();
                }}
              >
                {it.label}
              </button>
            ),
          ])}
        </div>
      )}
    </>
  );
}

const moreBtn: CSSProperties = {
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  cursor: 'pointer',
  padding: 0,
};

const menu: CSSProperties = {
  position: 'fixed',
  zIndex: 200,
  minWidth: 140,
  padding: 4,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-card)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-md)',
};

const sep: CSSProperties = {
  border: 0,
  borderTop: '1px solid var(--border-subtle)',
  margin: '4px 6px',
};
